import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper as OriginSwiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import Swiper from "swiper";

import { isArrayEmpty } from "../../../libs/common";
import { getTimeDiffString } from "../../../libs/dateFormat";
import { formatPostTitle } from "../../../libs/talk";

import { TalkListItem } from "../../../services/talkServices";

import { PostFooter } from "../../../pages/Talk/Detail";
import { PostListNoPostIcon } from "../../Talk/PostList";
import { Skeleton } from "../../../pages/Talk";

type Props = {
  postList: TalkListItem[];
  onReload?: () => void;
  type?: "default" | "view";
  isLoading?: boolean;
  useCategory?: boolean;
};

type SwiperProps = {
  spaceBetween?: number;
  slidesPerView?: number;
  slidesOffsetBefore?: number;
};

export default function PostSwiper({
  postList,
  onReload,
  isLoading,
  type = "default",
  useCategory = true,
}: Props) {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const swiperType: { [key: string]: SwiperProps } = {
    default: {
      spaceBetween: 8,
      slidesPerView: 1.3,
      slidesOffsetBefore: 22,
    },
    view: {
      spaceBetween: 8,
      slidesPerView: 1.3,
    },
  };
  const [swiper, setSwiper] = useState<Swiper>();

  const handleChange = (swiper: any) => {
    setActiveIndex(swiper.activeIndex);
  };

  useEffect(() => {
    if (!isArrayEmpty(swiper?.slides)) {
      swiper?.slideTo(0);
    }
  }, [swiper?.slides]);

  if (postList && postList.length === 0) {
    return (
      <div className="flex flex-col  items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
        <PostListNoPostIcon />
        게시글이 없어요
      </div>
    );
  }

  return (
    <div className="relative">
      <OriginSwiper
        onSlideChange={handleChange}
        onSwiper={(swiper) => setSwiper(swiper)}
        className="h-fit w-full"
        {...swiperType[type]}
      >
        {postList?.map((post) =>
          isLoading ? (
            <SwiperSlide key={post.lifePostId}>
              <Skeleton className="w-[290px] h-[200px] rounded" />
            </SwiperSlide>
          ) : (
            <SwiperSlide key={post.lifePostId}>
              <div className="p-px">
                <PostSwiperItem
                  post={post}
                  onClickLike={() => {
                    onReload?.();
                  }}
                  profileCount={
                    type === "default" ? 3 : window.innerWidth > 330 ? 1 : 0
                  }
                  useCategory={useCategory}
                />
              </div>
            </SwiperSlide>
          )
        )}
      </OriginSwiper>
      <div className="absolute w-full top-full z-10 flex gap-1 mt-2.5 justify-center">
        {postList?.map((_, index) => (
          <div
            key={index}
            className={`w-[6px] h-[6px] rounded-full ${
              activeIndex === index ? "bg-black" : "bg-gray-300"
            }`}
          ></div>
        ))}
      </div>
    </div>
  );
}

export const PostSwiperItem = ({
  post,
  onClickLike,
  profileCount,
  useCategory = true,
}: {
  post: TalkListItem;
  onClickLike?: (id: number) => void;
  profileCount?: number;
  useCategory?: boolean;
}) => {
  const navigate = useNavigate();
  const fileList = post.fileInfo?.items?.filter(
    (item) => item.scale === "small"
  );
  const plainContent =
    new DOMParser().parseFromString(post.content, "text/html").body
      .textContent || "";

  const shortContent = plainContent
    .split("\n")
    .slice(0, 3)
    .join("\n")
    .slice(0, 60);

  return (
    <div
      key={post.lifePostId}
      className="rounded-xl p-5 flex flex-col gap-4"
      style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.08)" }}
    >
      <div
        onClick={() => navigate(`/talk/${post.lifePostId}`)}
        className="flex flex-col gap-2.5"
      >
        <div className="flex justify-between items-start">
          <div className="text-base font-medium max-w-[70%] overflow-hidden whitespace-nowrap text-ellipsis flex flex-col gap-2.5">
            {useCategory && post.categoryNameM && (
              <div className=" rounded-full bg-[#ffeefb] px-2 py-2.5 h-5 flex items-center text-[11px] font-medium leading-4 text-[#e91078] w-fit">
                {post.categoryNameM}
              </div>
            )}
            {formatPostTitle(post.title)}
          </div>
          {post.createdAt && (
            <div className=" text-neutral-500 text-xs font-medium">
              {getTimeDiffString(post.createdAt)}
            </div>
          )}
        </div>
        <div className="flex gap-2.5 justify-between min-h-[80px]">
          <div className=" text-xs text-brand-neutral-40 whitespace-pre-line">
            {shortContent}
            {shortContent === plainContent ? "" : "..."}
          </div>
          {fileList && fileList.length > 0 && (
            <div className="h-[80px] w-[80px] relative">
              <div className="h-[80px] w-[80px] flex items-center justify-center">
                <img
                  src={fileList[0].url}
                  alt="img"
                  className="object-cover h-[80px] w-[80px]"
                />
              </div>
              {fileList.length > 1 && (
                <div className="absolute bottom-0 right-0 px-[5px] py-1 text-[10px] font-semibold bg-brand-primary-gray-100 bg-opacity-40 text-white gap-[5px] flex items-center">
                  <span>{fileList.length - 1}</span>
                  <span>+</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <PostFooter
        post={post}
        type="post"
        onClickLike={onClickLike}
        profileCount={profileCount}
        unUseReport
      />
    </div>
  );
};
